import React from "react";
import { ChevronRight, Lock, TrendingUp, Activity } from "lucide-react";
import {
  ImageForty,
  ImageFortyOne,
  ImageThirtyEight,
  ImageThirtyNine,
} from "../assets/images";

const TwelveTab = () => {
  // Mock data for the wellness trend chart
  const generateWaveData = () => {
    const days = ["M", "T", "W", "T", "F", "S"];
    const moodData = [0.3, 0.2, 0.4, 0.8, 0.6, 0.5];
    const fatigueData = [0.2, 0.1, 0.3, 0.9, 0.4, 0.8];

    return { days, moodData, fatigueData };
  };

  const { days, moodData, fatigueData } = generateWaveData();

  // SVG path generator for smooth curves
  const generateSmoothPath = (data, width = 100, height = 100) => {
    if (data.length === 0) return "";

    const points = data.map((y, x) => [
      (x / (data.length - 1)) * width,
      height - y * height,
    ]);

    let path = `M ${points[0][0]} ${points[0][1]}`;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];

      const cp1x = prev[0] + (curr[0] - prev[0]) * 0.3;
      const cp1y = prev[1];
      const cp2x = curr[0] - (next ? (next[0] - curr[0]) * 0.3 : 0);
      const cp2y = curr[1];

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr[0]} ${curr[1]}`;
    }

    return path;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w mx-auto">
        {/* Header */}
        <div className="mb-8 text-center lg:text-left">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Doctor Dashboard – Bridging Home & Clinic
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            A secure, intelligent view of patient wellness –
            <br className="hidden sm:block" />
            turning data into better recovery outcomes.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {/* Wellness Trend Chart */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">
              Wellness Trend
            </h2>

            {/* Chart Area */}
            <div className="relative h-56 sm:h-64 mb-4">
              <svg
                className="w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {/* Mood line (green) */}
                <path
                  d={generateSmoothPath(moodData, 100, 100)}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                  className="drop-shadow-sm"
                />

                {/* Fatigue line (pink) */}
                <path
                  d={generateSmoothPath(fatigueData, 100, 100)}
                  fill="none"
                  stroke="#f472b6"
                  strokeWidth="2"
                  className="drop-shadow-sm"
                />

                {/* Data points */}
                {moodData.map((value, index) => (
                  <circle
                    key={`mood-${index}`}
                    cx={(index / (moodData.length - 1)) * 100}
                    cy={100 - value * 100}
                    r="1.8"
                    fill="#10b981"
                  />
                ))}

                {fatigueData.map((value, index) => (
                  <circle
                    key={`fatigue-${index}`}
                    cx={(index / (fatigueData.length - 1)) * 100}
                    cy={100 - value * 100}
                    r="1.8"
                    fill="#f472b6"
                  />
                ))}
              </svg>
            </div>

            {/* X-axis labels */}
            <div className="flex justify-between text-xs sm:text-sm text-gray-500">
              {days.map((day, index) => (
                <span key={index}>{day}</span>
              ))}
            </div>

            {/* Secure Patient Data Section */}
            <div className="mt-8 pt-6 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-gray-900">
                  Secure Patient Data
                </h3>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Consent granted: Active</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Actionable Escalations */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">
              Actionable Escalations
            </h2>

            <div className="space-y-4">
              {/* Fatigue spike */}
              <div className="flex items-center justify-between p-4 bg-pink-50 rounded-xl border border-pink-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-pink-200 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-pink-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      Fatigue Spike
                    </div>
                    <div className="text-sm text-gray-600">
                      Likely from 6/10 → 9/10
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>

              {/* Mood decline */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      Mood Decline
                    </div>
                    <div className="text-sm text-gray-600">
                      Dropped below baseline
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>

              {/* Pain escalation */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      Pain Escalation
                    </div>
                    <div className="text-sm text-gray-600">
                      Likely from 6/10 → 9/10
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <FeatureCard
            imagePath={ImageThirtyEight}
            title="Actionable Escalations"
            desc="Timely insights for long-term monitoring"
            bg="bg-pink-100"
          />
          <FeatureCard
            imagePath={ImageThirtyNine}
            title="Wellness Trends"
            desc="Clear patterns for treatment planning"
            bg="bg-green-100"
          />
          <FeatureCard
            imagePath={ImageFortyOne}
            title="Secure Data"
            desc="HIPAA-grade consent-based access"
            bg="bg-blue-100"
          />
          <FeatureCard
            imagePath={ImageForty}
            title="Behavioral Flags"
            desc="Instant alerts for fatigue, mood, or pain spikes"
            bg="bg-yellow-100"
          />
        </div>

        {/* Bottom Description and CTA */}
        <div className="text-center">
          <p className="text-base sm:text-lg text-gray-700 mb-6">
            Designed to empower doctors with empathy-driven analytics.
          </p>
          <button className="bg-green-700 hover:bg-green-800 text-white px-6 sm:px-8 py-3 rounded-full font-medium transition-colors duration-200">
            Explore Doctor Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ imagePath, title, desc }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border text-center">
    <div
      className={`w-12 h-12  rounded-full flex items-center justify-center mx-auto mb-4`}
    >
      <img src={imagePath} alt={title} className="w-12 h-12 object-contain" />
    </div>
    <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{desc}</p>
  </div>
);

export default TwelveTab;
